import React from 'react';
import { ReportErrors } from '@jira/platform__error-reporting/src/ReportErrors.tsx';
import { AnalyticsSource } from '@atlassian/jira-analytics-web-react/src/components/decorators.tsx';
import { ErrorFlagRenderer } from '@atlassian/jira-error-boundary-flag-renderer/src/ErrorBoundaryFlag.tsx';
import { SCREEN } from '@atlassian/jira-product-analytics-bridge';
import {
	appBundleReadyMark,
	prefetchApiReadyMark,
} from '@atlassian/jira-providers-spa-apdex-analytics/src/marks.tsx';
import SubmitApdex from '@atlassian/jira-providers-spa-apdex-analytics/src/submit-apdex-mark/index.tsx';
import AddProjectToHistory from '@atlassian/jira-spa-apps-common/src/add-project-to-history/index.tsx';
import { PageLayout } from '@atlassian/jira-spa-apps-page-layout/src/page/index.tsx';
import { SpaStatePageReady } from '@atlassian/jira-spa-state-controller/src/components/main.tsx';
import UFOSegment from '@atlassian/jira-ufo-segment/src/index.tsx';
import { projectComponentsPageLoad } from '../utils/performance-analytics/index.tsx';
import type { Props } from './types.tsx';

// Replace with lodash/noop
// eslint-disable-next-line @typescript-eslint/no-empty-function
const noop = () => {};

function ProjectComponents({
	Updater,
	ProjectDirectoriesApp,
	tenantContext,
	projectContext,
	prefetchResult,
	query,
	onQueryChanged,
}: Props): React.JSX.Element | null {
	const { error: prefetchError, data } = prefetchResult;
	const { data: project } = projectContext;

	if (prefetchError) {
		// @ts-expect-error - react-resource-router types return Record<string,any> but ErrorFlagRenderer only accepts Errors
		return <ErrorFlagRenderer id="project-components" error={prefetchError} />;
	}

	if (!data || !(project && project.projectKey)) {
		return null;
	}
	const { isAdmin, locale } = tenantContext;

	return (
		<PageLayout>
			<UFOSegment name="project-components-page">
				<ReportErrors
					id="unhandled"
					packageName="jiraSpaAppsProjectComponents"
					teamName="morphinecake"
					sendToPrivacyUnsafeSplunk
				>
					<Updater />
					<ProjectDirectoriesApp
						locale={locale}
						projectKey={project.projectKey}
						projectId={project.projectId}
						projectName={project.projectName}
						projectType={project.projectType}
						isAdmin={isAdmin || project.isProjectAdmin === true || false}
						prefetchedData={data}
						onUnmount={noop}
						query={query}
						onQueryChanged={onQueryChanged}
					/>
					{/* @ts-expect-error - This is providing completely different metric prop than the SubmitApdex wants. How does it even work? */}
					<SubmitApdex
						metricKey="jira.project.components"
						metric={projectComponentsPageLoad}
						isExpectedToHaveSsr
						withMarks={[appBundleReadyMark, prefetchApiReadyMark]}
					/>
					<AddProjectToHistory projectKey={project.projectKey} />
					<SpaStatePageReady />
				</ReportErrors>
			</UFOSegment>
		</PageLayout>
	);
}
export default AnalyticsSource('project-components', SCREEN)(ProjectComponents);
